// @flow
import React from 'react';
import injectSheet from 'react-jss';
import moment from 'moment';
import io from 'socket.io-client';
import _ from 'shared/lib/gettext';
import isEmpty from 'lodash/isEmpty';
import Modal from '~/app/components/Modal';
import getNextVersion from '~/lib/getNextVersion';
import { Page, PageHeader, Section, Card } from '~/app/ui/Page';
import { RowMenu } from '~/app/ui/Menu';
import StatusBadge from '~/app/ui/StatusBadge';
import { S, A } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import DeploymentHistory from './DeploymentHistory';
import PendingDeployment from './PendingDeploymentContainer';
import DeployMode from './DeployModeContainer';
import ScheduledDeployment from './ScheduledDeployment';
import { getAppURL } from 'shared/lib/clientSettings';
import { WEBSOCKET_MSG_ID } from 'shared/constants';

type Deployment = {
  version: string,
  timestamp: number,
  gitCommitSHA: string,
  deployedBy?: { username: string }
}

type Props = {
  classes: Object,
  className?: string,
  activeDeployment: Deployment,
  pendingDeployment: {
    added: Deployment[],
    deleted: Deployment[],
    removed: Deployment[],
  },
  scheduledDeployment: Array<Deployment>,
  deploymentHistory: Array<Deployment>,
  recentHistory: Array<Deployment>,
  recentHistoryLoading: boolean,
  versionLoading: boolean,
  canShowOlder: boolean,
  canRedeployAll: boolean,
  onShowOlder: () => void,
  refetchDeploymentHistory: () => void,
  refetchPaginatedDeploymentHistory: () => void,
  refetchPendingDeployment: () => void
};

type State = {
  modalOpen: boolean,
  version: string,
  progress: number,
  deploying: boolean
}

/**
 * Deploy: what's live, what's waiting to go live, what's scheduled, and what
 * went out before. Redeploying the whole site is in the ⋯ menu, behind a
 * warning, because it rebuilds every page.
 */
class Deploy extends React.Component<Props, State> {
  props: Props = this.props;

  state = {
    modalOpen: false,
    version: '',
    progress: 0,
    deploying: false
  };

  componentDidMount() {
    const { activeDeployment, scheduledDeployment, deploymentHistory } = this.props;

    // Socket used for Deploy page deployment status
    this.socket = io(getAppURL());

    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_STATE, ({ deploying, progress }) => {
      this.setState({ deploying, progress });
    });

    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_PROGRESS, ({ progress }) => {
      this.setState({ progress, deploying: progress < 1 });
      // Any deployment that finishes (from this tab, another one or a schedule): show what's live,
      // what's still waiting and the history as they are now. The server sends 1 after saving the page states.
      if (progress >= 1) this.refreshAfterDeploy();
    });

    this.socket.on(WEBSOCKET_MSG_ID.DEPLOY_ERROR, (err) => {
      console.log('websocket error', err);
      this.setState({ progress: 0, deploying: false });
    });

    // Initialize next version. Necessary for when graphql responses are cached.
    this.setState({
      version: getNextVersion(activeDeployment, scheduledDeployment, deploymentHistory)
    });
  }

  componentWillUnmount() {
    this.socket.close();
  }

  componentDidUpdate(prevProps) {
    const { activeDeployment, scheduledDeployment, deploymentHistory } = this.props;

    // The history too: when it loads empty (nothing deployed yet) the live deployment stays null.
    if (activeDeployment !== prevProps.activeDeployment ||
      scheduledDeployment !== prevProps.scheduledDeployment ||
      deploymentHistory !== prevProps.deploymentHistory) {

      this.setState({
        version: getNextVersion(activeDeployment, scheduledDeployment, deploymentHistory)
      });
    }
  }

  refreshAfterDeploy = () => {
    const { refetchDeploymentHistory, refetchPendingDeployment, refetchPaginatedDeploymentHistory } = this.props;
    refetchDeploymentHistory();
    refetchPaginatedDeploymentHistory();
    refetchPendingDeployment();
  }

  onSuccessfulDeploy = () => {
    const { refetchDeploymentHistory, refetchPendingDeployment, refetchPaginatedDeploymentHistory } = this.props;

    // Refetching after the deployment is done.
    // The reason we have to trigger the refetch manually here is because
    // Node drops the graphql connection in 60 seconds by default and the
    // `refetchQueries` is triggered then.
    // When the deployment process takes longer than that, the `refetchQueries`
    // is fired before data is updated.
    refetchDeploymentHistory();
    refetchPaginatedDeploymentHistory();
    refetchPendingDeployment();

    this.setState({ modalOpen: false });
  }

  renderLive() {
    const { classes: c, activeDeployment: active, recentHistory } = this.props;
    const { deploying, progress } = this.state;
    if (isEmpty(active) && !deploying) return null;
    const at = active && moment(active.timestamp, 'x');
    // The versions list doesn't carry who deployed; the recent history does.
    const record = active && (recentHistory || []).find(d => d.version === active.version);
    const who = record && record.deployedBy && record.deployedBy.username;
    return (
      <Card className={c.live} testId="deploy-live">
        <div className={c.liveTop}>
          {deploying
            ? <StatusBadge tone="amber" label={`Deploying… ${Math.round((progress || 0) * 100)}%`} />
            : <StatusBadge tone="green" label="Live on the site" />}
          {active && <span className={c.liveVersion}>v{active.version}</span>}
        </div>
        {deploying && (
          <div className={c.bar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((progress || 0) * 100)} aria-label="Deployment progress">
            <div className={c.barFill} style={{ width: `${Math.max(2, Math.round((progress || 0) * 100))}%` }} />
          </div>
        )}
        {active && (
          <dl className={c.meta}>
            <div>
              <dt>Deployed</dt>
              <dd title={at.format('dddd, MMMM D, YYYY h:mm:ss A')}>{at.format('ddd, MMM D, h:mm A')} <span className={c.muted}>({at.fromNow()})</span></dd>
            </div>
            {who && (
              <div>
                <dt>By</dt>
                <dd>{who}</dd>
              </div>
            )}
            <div>
              <dt>Commit</dt>
              <dd className={c.mono}>{active.gitCommitSHA || '—'}</dd>
            </div>
          </dl>
        )}
      </Card>
    );
  }

  render() {
    const {
      classes: c, pendingDeployment, scheduledDeployment, recentHistory, recentHistoryLoading, canShowOlder, onShowOlder, canRedeployAll
    } = this.props;
    const { modalOpen, version, deploying } = this.state;

    const redeployAllConfirm = modalOpen && (
      <Modal
        onClose={() => this.setState({ modalOpen: false })}
        modalStyle="simple"
        title="Redeploy the whole site"
        disableFooter
      >
        <div className={c.deployModalBody}>
          <p className={c.warn}>{_('REDEPLOY_ALL_CONFIRM')}</p>
          <p className={c.note}>{_('SCHEDULED_REBUILD')}</p>
          <DeployMode
            deployMode="ALL"
            cancelBtnEnabled
            version={version}
            onCancel={() => this.setState({ modalOpen: false })}
            onSuccessDeploy={this.onSuccessfulDeploy}
          />
        </div>
      </Modal>
    );

    return (
      <Page testId="deploy-screen">
        <PageHeader
          title={tr('Deploy', '部署')}
          description="Approved changes wait here until they’re deployed. Each deployment gets the next version number."
          actions={canRedeployAll ? (
            <RowMenu
              label="More deploy actions"
              testId="deploy-more"
              groups={[[{
                key: 'redeploy-all',
                label: 'Redeploy the whole site…',
                icon: 'sync',
                hint: 'Rebuilds every live page. Slow; only after site-wide changes.',
                danger: true,
                disabled: deploying,
                onSelect: () => this.setState({ modalOpen: true })
              }]]}
            />
          ) : undefined}
        />
        {this.renderLive()}
        <PendingDeployment
          version={version}
          pendingDeployment={pendingDeployment}
          scheduledDeployment={scheduledDeployment}
          deployButtonDisabled={deploying}
          versionLoading={this.props.versionLoading}
          onDeployed={this.refreshAfterDeploy}
        />
        {!isEmpty(scheduledDeployment) &&
          <ScheduledDeployment
            scheduledDeployments={scheduledDeployment}
          />
        }
        <Section title="History" hint="Most recent first" testId="deploy-history">
          <DeploymentHistory
            deploymentHistory={recentHistory}
            isFetching={recentHistoryLoading}
          />
          {canShowOlder && (
            <div className={c.older}>
              <button type="button" className={c.olderButton} onClick={onShowOlder} disabled={recentHistoryLoading} data-testid="history-older">
                {recentHistoryLoading ? 'Loading…' : 'Show older deployments'}
              </button>
            </div>
          )}
        </Section>
        {redeployAllConfirm}
      </Page>
    );
  }
}

const styles = {
  live: { marginBottom: 28 },
  liveTop: { display: 'flex', alignItems: 'center', gap: 12 },
  liveVersion: { fontFamily: S.mono, fontSize: 20, fontWeight: 500, letterSpacing: '-0.01em' },
  bar: { height: 6, margin: '14px 0 2px', borderRadius: 3, background: S.chromeHover, overflow: 'hidden' },
  barFill: { height: '100%', background: S.amber, borderRadius: 3, transition: 'width 400ms ease' },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 40px',
    margin: '14px 0 0',
    '& div': { display: 'grid', gap: 2 },
    '& dt': { fontSize: 12, color: S.muted },
    '& dd': { margin: 0, fontSize: 14 }
  },
  muted: { color: S.muted },
  mono: { fontFamily: S.mono, fontSize: '13px !important' },
  deployModalBody: { width: '100%', padding: '20px 24px', fontFamily: S.ui, color: S.fg },
  warn: { margin: '0 0 8px', fontSize: 14, lineHeight: 1.5 },
  note: { margin: '0 0 16px', fontSize: 13, lineHeight: 1.5, color: S.amber },
  older: { display: 'flex', justifyContent: 'center', marginTop: 12 },
  olderButton: {
    all: 'unset',
    padding: '8px 14px',
    borderRadius: A.radius,
    fontFamily: S.ui,
    fontSize: 14,
    fontWeight: 600,
    color: S.fg,
    cursor: 'pointer',
    '&:hover': { background: S.chromeHover },
    '&:focus-visible': A.focus,
    '&[disabled]': { color: S.muted, cursor: 'default' }
  }
};

export default injectSheet(styles)(Deploy);
